import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { formatDateTime } from '../../../config/appConfig';
import { cn } from '../../../lib/utils';
import type { SystemHealthSnapshot } from '../types';
import SectionCard from './SectionCard';

interface SecurityInfraCardProps {
  security?: SystemHealthSnapshot['security'];
  postgres?: SystemHealthSnapshot['postgres'];
}

const SecurityInfraCard: React.FC<SecurityInfraCardProps> = ({ security, postgres }) => {
  const { colors } = useTheme();
  if (!security && !postgres) return null;

  const pgActive = postgres?.activeConnections ?? 0;
  const pgMax = postgres?.maxConnections;
  const pgPressure =
    pgMax != null && pgMax > 0 && postgres?.activeConnections != null
      ? Math.round((postgres.activeConnections / pgMax) * 100)
      : null;

  return (
    <SectionCard zone="security" title="Security & database" subtitle="Failed logins and Postgres connection pool">
      {security && (
        <div className="mb-4">
          <div className="flex items-baseline gap-2">
            <p
              className={cn(
                'text-2xl font-bold tabular-nums',
                security.failedLogins24h > 0 ? 'text-rose-700 dark:text-rose-300' : colors.text,
              )}
            >
              {security.failedLogins24h}
            </p>
            <p className={cn('text-sm', colors.textMuted)}>failed logins (24h)</p>
          </div>
          {security.recentFailedLogins.length > 0 && (
            <ul className={cn('mt-3 space-y-1.5 text-xs', colors.textMuted)}>
              {security.recentFailedLogins.map((a) => (
                <li key={`${a.at}-${a.email}`} className="flex flex-wrap gap-x-2">
                  <span>{formatDateTime(a.at)}</span>
                  <span className={colors.text}>{a.email}</span>
                  <span>— {a.reason}</span>
                </li>
              ))}
            </ul>
          )}
          <p className={cn('text-[10px] mt-2', colors.textMuted)}>
            In-memory counter (resets on backend restart). Successful logins are not listed.
          </p>
        </div>
      )}

      {postgres && (
        <div className={cn('pt-4 border-t', colors.border)}>
          <p className={cn('text-sm font-medium mb-2', colors.text)}>Postgres connections</p>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div>
              <p className={colors.textMuted}>Active</p>
              <p className={cn('font-bold tabular-nums', colors.text)}>{postgres.activeConnections ?? '—'}</p>
            </div>
            <div>
              <p className={colors.textMuted}>Idle</p>
              <p className={cn('font-bold tabular-nums', colors.text)}>{postgres.idleConnections ?? '—'}</p>
            </div>
            <div>
              <p className={colors.textMuted}>Max</p>
              <p className={cn('font-bold tabular-nums', colors.text)}>{postgres.maxConnections ?? '—'}</p>
            </div>
          </div>
          {pgPressure != null && (
            <p className={cn('text-xs mt-2', pgPressure >= 80 ? 'text-amber-700 dark:text-amber-300' : colors.textMuted)}>
              {pgActive} active of {pgMax} max ({pgPressure}% of pool)
            </p>
          )}
        </div>
      )}
    </SectionCard>
  );
};

export default SecurityInfraCard;
